import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Building2, Check, ChevronDown, ListFilter, MapPin, Plus, Search, SlidersHorizontal, X,
} from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

/** A listing the caller may put on the board — mirrors AddableListing in
 *  app/schemas/deal_pipeline.py. */
interface AddableListing {
  listing_id: string;
  title: string | null;
  reference_code: string | null;
  industry_category: string | null;
  asking_price: number | null;
  revenue: number | null;
  make_offer: boolean;
  status: string | null;
  city_name: string | null;
  image_url: string | null;
}

/** One page of the picker — mirrors AddableListingPage. */
interface AddablePage {
  total: number;
  unfiltered_total: number;
  page: number;
  page_size: number;
  data: AddableListing[];
}

interface LookupItem {
  id: number;
  name: string;
}

const PAGE_SIZE = 20;

function money(p: number | null): string {
  if (p == null) return "—";
  if (p >= 1_000_000_000) return `$${(p / 1_000_000_000).toFixed(1)}B`;
  if (p >= 1_000_000) return `$${(p / 1_000_000).toFixed(1)}M`;
  if (p >= 1_000) return `$${(p / 1_000).toFixed(0)}K`;
  return `$${p.toLocaleString()}`;
}

function priceLabel(l: AddableListing): string {
  if (l.make_offer && l.asking_price == null) return "Make Offer";
  return money(l.asking_price);
}

/** "under_review" → "Under review". The lookup table stores slugs. */
const prettyStatus = (slug: string): string =>
  slug.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/** Statuses that mean the deal is already moving. Tinted so an advisor can see
 *  at a glance which listings are live versus still drafts. */
const LIVE_STATUSES = new Set(["active", "under_offer", "loi", "due_diligence", "escrow"]);
const CLOSED_STATUSES = new Set(["sold", "expired", "withdrawn", "rejected"]);

/** Price bands, as [min, max] in dollars. A bound excludes the "Make Offer"
 *  listings, which carry no asking price at all — the footnote says so. */
const PRICE_BANDS: { value: string; label: string; min?: number; max?: number }[] = [
  { value: "", label: "Any price" },
  { value: "lt250k", label: "Under $250K", max: 250_000 },
  { value: "250k-1m", label: "$250K – $1M", min: 250_000, max: 1_000_000 },
  { value: "1m-5m", label: "$1M – $5M", min: 1_000_000, max: 5_000_000 },
  { value: "5m-25m", label: "$5M – $25M", min: 5_000_000, max: 25_000_000 },
  { value: "gt25m", label: "$25M+", min: 25_000_000 },
];

const SORTS: { value: string; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "price_desc", label: "Price: high → low" },
  { value: "price_asc", label: "Price: low → high" },
  { value: "title", label: "Title A–Z" },
];

/**
 * Compact dropdown for the filter bar.
 *
 * Deliberately NOT the shared SearchableSelect: that one portals its panel to
 * <body> at z-50, which sits *under* this modal's z-[100] backdrop and would
 * open invisibly. This panel is absolutely positioned inside the modal's own
 * (non-scrolling) filter bar instead, so it layers over the result list
 * without any portal or z-index coordination at all.
 */
function FilterSelect({
  label,
  value,
  options,
  onChange,
  icon: Icon,
  className,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  icon?: typeof ListFilter;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes the dropdown only — the modal's own handler must not also
    // fire and tear the whole picker down under the user.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const active = Boolean(value);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-lg border px-3 text-sm transition-colors",
          active
            ? "border-primary/50 bg-primary/10 text-foreground"
            : "border-border/80 bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground",
        )}
      >
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />}
        <span className={cn("min-w-0 flex-1 truncate text-left", active && "font-semibold")}>
          {selected?.label ?? label}
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+0.3rem)] z-30 max-h-64 overflow-y-auto rounded-lg border border-border/70 bg-card p-1 shadow-2xl"
        >
          {options.map((o) => (
            <button
              key={o.value || "__all"}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                o.value === value
                  ? "bg-primary/15 font-semibold text-foreground"
                  : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
              )}
            >
              <Check className={cn("h-3.5 w-3.5 shrink-0", o.value === value ? "opacity-100 text-primary" : "opacity-0")} />
              <span className="truncate">{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the created card so the board can drop it in without a refetch. */
  onCreated: (card: unknown) => void;
}

/**
 * "Add Deal" picker — the listings the caller manages that are not already on
 * the board, so an advisor can start tracking one before any buyer surfaces.
 * The created card carries no buyer (see the listing-only card in
 * app/services/deal_pipeline.py).
 *
 * Searching, filtering, sorting and paging all happen server-side: an admin
 * manages every listing on the platform (700+), so the picker can never hold
 * the full set and narrow it locally.
 */
export default function AddDealModal({ open, onClose, onCreated }: Props) {
  const toast = useToast();
  const [page, setPage] = useState<AddablePage | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<AddableListing | null>(null);

  // Filters. `pageNum` is 1-based to match the shared Pagination component.
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [band, setBand] = useState("");
  const [sort, setSort] = useState("newest");
  const [pageNum, setPageNum] = useState(1);

  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [statuses, setStatuses] = useState<LookupItem[]>([]);

  // Fresh picker each time it opens — a stale filter from last time silently
  // hides most of the book of business.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setCategory("");
    setStatus("");
    setBand("");
    setSort("newest");
    setPageNum(1);
    setSelected(null);
  }, [open]);

  // Lookups are small, cached server-side and never change mid-session, so they
  // load once on first open rather than on every open.
  useEffect(() => {
    if (!open || categories.length || statuses.length) return;
    void Promise.all([
      api.get<LookupItem[]>("/lookups/categories").catch(() => [] as LookupItem[]),
      api.get<LookupItem[]>("/lookups/listing-statuses").catch(() => [] as LookupItem[]),
    ]).then(([cats, sts]) => {
      setCategories(cats);
      setStatuses(sts);
    });
  }, [open, categories.length, statuses.length]);

  /** Every filter change must send the user back to page 1: staying on page 30
   *  of a set the new filter narrowed to two pages shows an empty list that
   *  looks like "no results". Both setters run in the same event handler, so
   *  React batches them into one render and one fetch. */
  const applyFilter = useCallback((set: (v: string) => void) => (v: string) => {
    set(v);
    setPageNum(1);
  }, []);

  const bandDef = PRICE_BANDS.find((b) => b.value === band);

  // One fetch per settled filter state. The query string is built here rather
  // than inside the effect so the effect's dependency is a single stable string
  // — otherwise every render rebuilds the params object and refetches forever.
  const query = useMemo(() => {
    const p = new URLSearchParams();
    const q = search.trim();
    if (q) p.set("search", q);
    if (category) p.set("category_id", category);
    if (status) p.set("status_id", status);
    if (bandDef?.min != null) p.set("min_price", String(bandDef.min));
    if (bandDef?.max != null) p.set("max_price", String(bandDef.max));
    p.set("sort", sort);
    p.set("page", String(pageNum));
    p.set("page_size", String(PAGE_SIZE));
    return p.toString();
  }, [search, category, status, bandDef, sort, pageNum]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    // Typing debounces; changing a dropdown or a page does not.
    const timer = setTimeout(() => {
      api
        .get<AddablePage>(`/deal-pipeline/addable-listings?${query}`)
        .then((data) => { if (!cancelled) setPage(data); })
        .catch((e) => {
          if (cancelled) return;
          toast.error(e instanceof Error ? e.message : "Failed to load your listings");
          setPage(null);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search.trim() ? 300 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // toast is stable; including it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  const submit = useCallback(async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const card = await api.post("/deal-pipeline/deals", { listing_id: selected.listing_id });
      onCreated(card);
      toast.success("Deal added to the board");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add the deal");
    } finally {
      setSaving(false);
    }
  }, [selected, saving, onCreated, onClose, toast]);

  // Escape closes; Enter adds the highlighted listing.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (saving) return;
      if (e.key === "Escape") onClose();
      // Enter adds the selection, but not while the caret is in the search box
      // — typing and hitting Enter would otherwise add whatever was picked
      // several pages ago.
      const typing = (e.target as HTMLElement | null)?.tagName === "INPUT";
      if (e.key === "Enter" && selected && !typing) void submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose, selected, submit]);

  const filtersActive = Boolean(search.trim() || category || status || band);
  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setStatus("");
    setBand("");
    setPageNum(1);
  };

  const categoryOptions = useMemo(
    () => [{ value: "", label: "All industries" }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))],
    [categories],
  );
  const statusOptions = useMemo(
    () => [{ value: "", label: "Any status" }, ...statuses.map((s) => ({ value: String(s.id), label: prettyStatus(s.name) }))],
    [statuses],
  );

  const rows = page?.data ?? [];
  const total = page?.total ?? 0;

  if (!open) return null;

  /* Portalled to <body> so the overlay clears the nav and sidebar, and sized
     off the padded inset-0 parent rather than a vh unit — the app-wide
     zoom on <html> double-scales those inside a fixed portal. */
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={() => !saving && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add deal"
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/70 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Plus className="h-5 w-5" />
            </div>
            <div>
              <h3 className="heading-bold text-lg text-foreground">Add a deal</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Pick one of your listings to start tracking it on the board.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            aria-label="Close"
            className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filter bar. Always mounted: a filter that returns nothing must still
            leave the user the controls to undo it. Not scrollable, so the
            dropdown panels can layer over the result list below. */}
        <div className="shrink-0 border-b border-border/60 bg-muted/20 px-6 py-4">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPageNum(1); }}
                placeholder="Search by BL code or title…"
                className="h-10 pl-10 pr-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setPageNum(1); }}
                  title="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <FilterSelect
              label="All industries"
              value={category}
              options={categoryOptions}
              onChange={applyFilter(setCategory)}
              icon={Building2}
            />
            <FilterSelect
              label="Any status"
              value={status}
              options={statusOptions}
              onChange={applyFilter(setStatus)}
              icon={ListFilter}
            />
            <FilterSelect
              label="Any price"
              value={band}
              options={PRICE_BANDS}
              onChange={applyFilter(setBand)}
            />
            <FilterSelect
              label="Newest first"
              value={sort}
              options={SORTS}
              onChange={applyFilter(setSort)}
              icon={SlidersHorizontal}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
            <span>
              <span className="font-semibold tabular-nums text-foreground">{total.toLocaleString()}</span>
              {page && total !== page.unfiltered_total && (
                <> of <span className="tabular-nums">{page.unfiltered_total.toLocaleString()}</span></>
              )}{" "}
              listing{total === 1 ? "" : "s"} available to add
            </span>
            {bandDef?.value && (
              <span className="text-muted-foreground/70">· a price band excludes “Make Offer” listings</span>
            )}
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto font-semibold text-primary transition-colors hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <Loader className="min-h-[16rem]" />
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60">
                <Building2 className="h-7 w-7 text-muted-foreground/40" />
              </div>
              <p className="mt-4 font-semibold text-foreground">
                {filtersActive ? "No listings match these filters" : "Nothing left to add"}
              </p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                {filtersActive
                  ? "Widen the search, industry, status or price band to see more."
                  : "Every listing you manage is already on the board."}
              </p>
              {filtersActive && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {rows.map((l) => {
                const active = selected?.listing_id === l.listing_id;
                const live = l.status ? LIVE_STATUSES.has(l.status) : false;
                const closed = l.status ? CLOSED_STATUSES.has(l.status) : false;
                return (
                  <li key={l.listing_id}>
                    <button
                      type="button"
                      onClick={() => setSelected(active ? null : l)}
                      aria-pressed={active}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                        active
                          ? "border-primary/60 bg-primary/5"
                          : "border-border/70 hover:border-primary/30 hover:bg-accent/40",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                          active ? "border-primary bg-primary text-primary-foreground" : "border-border",
                        )}
                      >
                        {active && <Check className="h-3 w-3" />}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {l.reference_code && (
                            <span className="font-mono text-[0.7rem] text-muted-foreground">
                              {l.reference_code}
                            </span>
                          )}
                          {l.industry_category && (
                            <span className="truncate text-[0.62rem] font-semibold uppercase tracking-wider text-primary/70">
                              {l.industry_category}
                            </span>
                          )}
                          {l.status && (
                            <span
                              className={cn(
                                "rounded-full px-1.5 py-px text-[0.6rem] font-bold uppercase tracking-wider",
                                live
                                  ? "bg-emerald-500/15 text-emerald-500"
                                  : closed
                                  ? "bg-rose-500/15 text-rose-400"
                                  : "bg-muted text-muted-foreground",
                              )}
                            >
                              {prettyStatus(l.status)}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 line-clamp-1 block text-sm font-semibold text-foreground">
                          {l.title || "Untitled listing"}
                        </span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[0.7rem] text-muted-foreground">
                          {l.city_name && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 shrink-0" />
                              {l.city_name}
                            </span>
                          )}
                          {l.revenue != null && l.revenue > 0 && (
                            <span>{money(l.revenue)} revenue</span>
                          )}
                        </span>
                      </span>

                      <span className="shrink-0 text-sm font-bold text-primary">{priceLabel(l)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination sits outside the scroll area so it is reachable without
            scrolling to the bottom of the page first. It tracks the REQUESTED
            page, not the last one that landed, so the highlight moves the
            instant the user clicks rather than when the fetch returns. */}
        {total > PAGE_SIZE && (
          <div className="shrink-0 border-t border-border/60 px-6 py-3">
            <Pagination
              page={pageNum}
              totalElements={total}
              pageSize={PAGE_SIZE}
              onChange={setPageNum}
              className="border-t-0 pt-0"
            />
          </div>
        )}

        {/* Footer — names the selected listing, which may be pages away by now. */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-border/70 px-6 py-4">
          {selected && (
            <p className="mr-auto min-w-0 truncate text-xs text-muted-foreground">
              Adding{" "}
              <span className="font-semibold text-foreground">
                {selected.reference_code ? `${selected.reference_code} · ` : ""}
                {selected.title || "Untitled listing"}
              </span>
            </p>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!selected || saving}>
            {saving ? "Adding…" : "Add to board"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
